import { useState, useEffect } from "react";
import { useMediaQuery } from "@mui/material";
import SummaryCards from "./summaryCards";
import Filters from "./filters";
import KanbanBoard from "./kanbanBoard";
import BudgetHistoryModal from "./budgetHistoryModal";
import { Budget, BudgetViewType, BudgetSummary } from "./types";
import { mockSummary } from "./mockData";
import { fetchBudgetsKanban, fetchBudgetsKanbanSummary, fetchBudgetUserProfile } from "../../api/fetchBudget";
import { BudgetStatusFilterEnum } from "../../enums/budgetStatus";
import { getRouteByProfile, getKanbanRouteByProfile } from "./budgetCard/utils";
import { useNavigate } from "react-router-dom";
import { mapApiBudgetToBudget } from "./utils/budgetMapper";

interface BudgetsKanbanProps {
  persona?: string;
}

const BudgetsKanban = ({ persona }: BudgetsKanbanProps) => {
  const [budgets, setBudgets] = useState<Budget[]>([]);
  const [summary, setSummary] = useState<BudgetSummary>(mockSummary);
  const [viewType, setViewType] = useState<BudgetViewType>("geral");
  const [chassisFilter, setChassisFilter] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<BudgetStatusFilterEnum | undefined>(undefined);
  const [startDateFilter, setStartDateFilter] = useState<string | undefined>(undefined);
  const [endDateFilter, setEndDateFilter] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [showHistoryModal, setShowHistoryModal] = useState<boolean>(false);
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCreateBudget = async () => {
    const userProfile = await fetchBudgetUserProfile();
    const createRoute = getRouteByProfile(userProfile);
    const kanbanRoute = getKanbanRouteByProfile(userProfile);
    const redirectBack = encodeURIComponent(kanbanRoute);
    navigate(`${createRoute}?redirectBack=${redirectBack}`)
  };

  const handleChassisSearch = (chassis: string) => {
    setChassisFilter(chassis);
  };

  const handleStatusChange = (status?: BudgetStatusFilterEnum) => {
    setStatusFilter(status);
  };

  const handleDateRangeChange = (startDate?: string, endDate?: string) => {
    setStartDateFilter(startDate);
    setEndDateFilter(endDate);
  };

  const handleViewTypeChange = (newViewType: BudgetViewType) => {
    setViewType(newViewType);
  };

  useEffect(() => {
    const loadBudgets = async () => {
      setLoading(true);
      try {
        const baseFilters: any = {
          page: 1,
          limit: 50,
        };

        if (chassisFilter) {
          baseFilters.chassis = chassisFilter;
        }

        if (startDateFilter) {
          baseFilters.startUpdatedAt = `${startDateFilter}T00:00:00`;
        }

        if (endDateFilter) {
          baseFilters.endUpdatedAt = `${endDateFilter}T23:59:59`;
        }

        if (viewType === "cliente") {
          baseFilters.board = "MANAGER";
        } else if (viewType === "fabricante") {
          baseFilters.board = "FACTORY";
        }

        const [pendenteResponse, enviadoResponse, concluidoResponse, summaryResponse] = await Promise.all([
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "IN_REVIEW", status: statusFilter }),
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "PENDING", status: statusFilter }),
          fetchBudgetsKanban({ ...baseFilters, kanbanStep: "ACCEPTED", status: statusFilter }),
          fetchBudgetsKanbanSummary({ ...baseFilters, status: statusFilter }),
        ]);

        const allBudgets = [
          ...pendenteResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "IN_REVIEW" }, persona)),
          ...enviadoResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "PENDING" }, persona)),
          ...concluidoResponse.budgets.map((budget) => mapApiBudgetToBudget({ ...budget, kanbanStep: "ACCEPTED" }, persona)),
        ];

        setBudgets(allBudgets);
        setSummary(summaryResponse);
      } catch (error) {
        console.error("[BudgetsKanban] Erro ao carregar budgets:", error);
        setBudgets([]);
        setSummary(mockSummary);
      } finally {
        setLoading(false);
      }
    };

    loadBudgets();
  }, [chassisFilter, statusFilter, startDateFilter, endDateFilter, viewType, persona]);

  const handleViewHistory = (budgetId: string) => {
    setSelectedBudgetId(budgetId);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedBudgetId(null);
  };

  const handleAction = (budgetId: string, action: string) => {
    console.log("Ação no orçamento:", budgetId, action);
  };

  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px) and (min-width: 769px)");

  return (
    <div className={isMobile ? "p-3" : isTablet ? "p-4" : "p-5"}>
      <div className={isMobile ? "mb-3" : "mb-5"}>
        <SummaryCards summary={summary} loading={loading} persona={persona} />
      </div>

      <div className={isMobile ? "mb-3" : "mb-5"}>
        <h2 className={`${isMobile ? "text-xl" : "text-2xl"} font-bold ${isMobile ? "mb-3" : "mb-4"}`}>Orçamentos</h2>
        <Filters
          onCreateBudget={handleCreateBudget}
          onChassisSearch={handleChassisSearch}
          onStatusChange={handleStatusChange}
          onDateRangeChange={handleDateRangeChange}
          persona={persona}
        />
      </div>

      <div className={isMobile ? "mt-3" : "mt-5"}>
        <KanbanBoard
          budgets={budgets}
          viewType={viewType}
          onViewTypeChange={handleViewTypeChange}
          onViewHistory={handleViewHistory}
          onAction={handleAction}
          persona={persona}
        />
      </div>

      {selectedBudgetId && (
        <BudgetHistoryModal
          show={showHistoryModal}
          budgetId={selectedBudgetId}
          consultant={budgets.find((budget) => budget.id === selectedBudgetId)?.consultant || "Não identificado"}
          onClose={handleCloseHistoryModal}
        />
      )}
    </div>
  );
};

export default BudgetsKanban;
